import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { fotoUrl } from '../api';
import { useAuth } from '../AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const TR = (s: string) =>
  new Date(s).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
const SAAT = (s: string) => s?.slice(0, 5) ?? '';
const PARA = (n: number) => `₺${Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;

const SEV_RENK: Record<string, [string, string]> = {
  'Başlangıç': ['#E6F1FB', '#0C447C'],
  'Orta':      ['#FAEEDA', '#854F0B'],
  'İleri':     ['#EAF3DE', '#3B6D11'],
};

interface Egitmen {
  id: string; ad: string; soyad: string; email: string; tel: string;
  foto_url: string | null; uzmanlik: string[];
  bio: string | null; durum: string;
}
interface DersItem {
  id: string; ad: string; seviye: string; salon_adi: string;
  kapasite: number; hakedis_oran: number; kayitli_ogrenci: number;
  seanslar: { gun: string; baslangic_saat: string; bitis_saat: string }[];
}
interface HakedisDetay {
  tarih: string; ders_adi: string;
  katildi: number; gelmedi: number;
  hakedis: number; hakedis_oran: number;
}
interface Odeme {
  id: string; odeme_tarihi: string; odenen_tutar: number;
  odeme_tipi: string; odeme_yontemi: string;
  donem_baslangic: string; donem_bitis: string; aciklama: string;
}
interface Ozet { toplam_birikmis: number; toplam_odenen: number; bekleyen: number; hakedis_baslangic_bakiye: number; }
interface DersDetayOgrenci {
  id: string; ad: string; soyad: string; email: string; tel: string;
  foto_url: string | null; kayit_tarihi: string; kalan_hak: number | null; donduruldu: boolean;
}

type Sekme = 'dersler' | 'program' | 'hakedis' | 'odemeler';

const GUNLER = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const RENK_PALETI: [string, string][] = [
  ['#EEEDFE', '#534AB7'], ['#E6F1FB', '#0C447C'], ['#EAF3DE', '#3B6D11'],
  ['#FAEEDA', '#854F0B'], ['#FCEBEB', '#A32D2D'], ['#F0F9FF', '#0369A1'],
  ['#F5F0FF', '#7C3AED'], ['#FFF7ED', '#C2410C'],
];
const saatToMin = (s: string) => { const [h, m] = s.split(':').map(Number); return h * 60 + m; };
const GRID_BAS = 8 * 60;
const GRID_BIT = 22 * 60;
const SAAT_YUK = 56;

export default function EgitmenDetay() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { kullanici, subeGirisi } = useAuth();
  const isSuperAdmin = kullanici?.rol === 'super_admin' || subeGirisi !== null;
  const { isDark } = useTheme();
  const card        = isDark ? '#1a1a1a' : 'white';
  const cardAlt     = isDark ? '#1e1e1e' : '#f8f8f8';
  const cardHover   = isDark ? '#252525' : '#fafafa';
  const border      = isDark ? '#2a2a2a' : '#eee';
  const borderSub   = isDark ? '#202020' : '#f0f0f0';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec     = isDark ? '#999'    : '#888';
  const textMuted   = isDark ? '#666'    : '#aaa';

  const [egitmen, setEgitmen] = useState<Egitmen | null>(null);
  const [dersler, setDersler] = useState<DersItem[]>([]);
  const [hakedisDetay, setHakedisDetay] = useState<HakedisDetay[]>([]);
  const [odemeler, setOdemeler] = useState<Odeme[]>([]);
  const [ozet, setOzet] = useState<Ozet | null>(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [sekme, setSekme] = useState<Sekme>('dersler');

  // Ders detay drawer
  const [secilenDersId, setSecilenDersId] = useState<string | null>(null);
  const [dersDetay, setDersDetay] = useState<{ ders: any; ogrenciler: DersDetayOgrenci[] } | null>(null);
  const [dersDetayYuk, setDersDetayYuk] = useState(false);

  // Başlangıç bakiyesi
  const [bakiyeModal, setBakiyeModal] = useState(false);
  const [bakiyeInput, setBakiyeInput] = useState('');
  const [bakiyeKaydediliyor, setBakiyeKaydediliyor] = useState(false);

  const bakiyeKaydet = async () => {
    setBakiyeKaydediliyor(true);
    try {
      await api.patch(`/api/egitmenler/${id}/baslangic-bakiye`, { bakiye: parseFloat(bakiyeInput) || 0 });
      setBakiyeModal(false);
      yukle();
    } catch (err: any) { alert(err.response?.data?.hata || 'Hata'); }
    finally { setBakiyeKaydediliyor(false); }
  };

  // Ödeme formu
  const [odemeModal, setOdemeModal] = useState(false);
  const [odemeForm, setOdemeForm] = useState({
    odenen_tutar: '', aciklama: '',
  });
  const [odemeKaydediliyor, setOdemeKaydediliyor] = useState(false);
  const [odemeHata, setOdemeHata] = useState('');

  const yukle = () => {
    if (!id) return;
    setYukleniyor(true);
    Promise.all([
      api.get(`/api/egitmenler/${id}`),
      api.get(`/api/egitmenler/${id}/dersler`),
      api.get(`/api/hakedis/${id}`),
    ]).then(([eRes, dRes, hRes]) => {
      setEgitmen(eRes.data);
      setDersler(dRes.data);
      setHakedisDetay(hRes.data.detay ?? []);
      setOdemeler(hRes.data.odemeler ?? []);
      setOzet(hRes.data.ozet ?? null);
    }).catch(console.error)
      .finally(() => setYukleniyor(false));
  };

  useEffect(() => { yukle(); }, [id]);

  useEffect(() => {
    if (!secilenDersId) { setDersDetay(null); return; }
    setDersDetayYuk(true);
    api.get(`/api/dersler/${secilenDersId}`)
      .then(r => setDersDetay({ ders: r.data.ders, ogrenciler: r.data.ogrenciler ?? [] }))
      .catch(console.error)
      .finally(() => setDersDetayYuk(false));
  }, [secilenDersId]);

  const odemeKaydet = async () => {
    if (!odemeForm.odenen_tutar || parseFloat(odemeForm.odenen_tutar) <= 0) {
      setOdemeHata('Geçerli bir tutar giriniz'); return;
    }
    setOdemeKaydediliyor(true); setOdemeHata('');
    try {
      await api.post('/api/hakedis/ode', {
        egitmen_id: id,
        odenen_tutar: parseFloat(odemeForm.odenen_tutar),
        aciklama: odemeForm.aciklama || undefined,
      });
      setOdemeModal(false);
      setOdemeForm({ odenen_tutar: '', aciklama: '' });
      yukle();
    } catch (e: any) { setOdemeHata(e.response?.data?.hata ?? 'Kaydedilemedi'); }
    finally { setOdemeKaydediliyor(false); }
  };

  if (yukleniyor) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: textMuted, fontSize: 14 }}>
      Yükleniyor...
    </div>
  );
  if (!egitmen) return <div style={{ padding: 24, color: textMuted }}>Eğitmen bulunamadı.</div>;

  const bekleyen = ozet ? Number(ozet.bekleyen ?? (Number(ozet.toplam_birikmis) - Number(ozet.toplam_odenen))) : 0;

  return (
    <div style={{ padding: '24px 24px 48px' }}>

      {/* Geri */}
      <button onClick={() => navigate(-1)} style={{
        background: 'none', border: 'none', cursor: 'pointer', color: textSec, fontSize: 13,
        padding: '0 0 16px', display: 'flex', alignItems: 'center', gap: 6,
      }}>
        ← Geri
      </button>

      {/* Profil kartı */}
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', cursor: 'pointer', flexShrink: 0 }}
            onClick={() => (kullanici?.rol === 'admin' || kullanici?.id === id) && document.getElementById('egitmen-foto-input')?.click()}>
            {egitmen.foto_url
              ? <img src={fotoUrl(egitmen.foto_url)} alt="" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }} />
              : <div style={{ width: 80, height: 80, borderRadius: '50%', background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 700, color: '#534AB7' }}>
                  {egitmen.ad?.charAt(0)}{egitmen.soyad?.charAt(0)}
                </div>
            }
            {(kullanici?.rol === 'admin' || kullanici?.id === id) && (
              <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity .2s' }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '1')} onMouseLeave={e => (e.currentTarget.style.opacity = '0')}>
                <span style={{ color: 'white', fontSize: 20 }}>↑</span>
              </div>
            )}
          </div>
          <input id="egitmen-foto-input" type="file" accept="image/*" style={{ display: 'none' }}
            onChange={async e => {
              const file = e.target.files?.[0];
              if (!file) return;
              const formData = new FormData();
              formData.append('foto', file);
              try {
                await api.post(`/api/egitmenler/${id}/foto`, formData);
                yukle();
              } catch (err: any) { alert(err.response?.data?.hata || 'Foto yüklenemedi'); }
            }} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
              <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{egitmen.ad} {egitmen.soyad}</h1>
              {egitmen.durum === 'pasif' && (
                <span style={{ background: isDark ? '#222222' : '#f0f0f0', color: textSec, borderRadius: 20, padding: '3px 12px', fontSize: 12 }}>Pasif</span>
              )}
            </div>
            <div style={{ fontSize: 13, color: textSec, display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
              {egitmen.email && <span>✉ {egitmen.email}</span>}
              {egitmen.tel   && <span>📱 {egitmen.tel}</span>}
            </div>
            {egitmen.uzmanlik?.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {egitmen.uzmanlik.map((u, i) => (
                  <span key={i} style={{ background: isDark ? '#222222' : '#f0f0f0', color: isDark ? '#bbb' : '#555', borderRadius: 20, padding: '2px 10px', fontSize: 11 }}>{u}</span>
                ))}
              </div>
            )}
            {egitmen.bio && <div style={{ fontSize: 13, color: isDark ? '#999' : '#666', lineHeight: 1.6 }}>{egitmen.bio}</div>}
          </div>
          <button onClick={() => navigate('/egitmenler', { state: { duzenleId: egitmen.id } })}
            style={{ border: `1px solid ${border}`, background: card, borderRadius: 8, padding: '7px 16px', fontSize: 12, cursor: 'pointer', color: '#534AB7', flexShrink: 0 }}>
            ✎ Düzenle
          </button>
        </div>
      </div>

      {/* Özet istatistikler */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <StatKart baslik="Aktif ders" deger={String(dersler.length)} renk="#534AB7" bg="#EEEDFE" />
        <StatKart baslik="Toplam hakedis" deger={ozet ? PARA(Number(ozet.toplam_birikmis)) : '—'} renk="#1D9E75" bg="#EAF3DE" />
        <StatKart baslik="Ödenen" deger={ozet ? PARA(Number(ozet.toplam_odenen)) : '—'} renk="#3B6D11" bg="#EAF3DE" />
        <StatKart baslik="Bekleyen" deger={PARA(bekleyen)} renk={bekleyen > 0 ? '#854F0B' : '#3B6D11'} bg={bekleyen > 0 ? (isDark ? 'rgba(245,158,11,0.15)' : '#FAEEDA') : (isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE')} />
      </div>

      {/* Başlangıç bakiyesi — sadece super_admin */}
      {isSuperAdmin && (
        <div style={{ background: '#FAEEDA', border: '1px solid #f0d5a0', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <span style={{ fontSize: 12, color: '#854F0B', fontWeight: 600 }}>Devreden Bakiye (Önceki Sistem)</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#854F0B', marginLeft: 12 }}>
              {ozet ? PARA(Number(ozet.hakedis_baslangic_bakiye)) : '₺0,00'}
            </span>
          </div>
          <button onClick={() => { setBakiyeInput(String(ozet?.hakedis_baslangic_bakiye ?? 0)); setBakiyeModal(true); }}
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #e0a93b', background: 'white', color: '#854F0B', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            Düzenle
          </button>
        </div>
      )}

      {/* Sekmeler */}
      <div style={{ display: 'flex', gap: 4, background: 'white', borderRadius: 10, padding: 4, marginBottom: 14, border: '1px solid #eee' }}>
        {([
          { key: 'dersler',  label: kullanici?.id === id ? 'Derslerim' : 'Dersler',   badge: dersler.length },
          { key: 'program',  label: kullanici?.id === id ? 'Programım' : 'Program',   badge: 0 },
          { key: 'hakedis',  label: 'Hakedis geçmişi',                                badge: hakedisDetay.length },
          { key: 'odemeler', label: 'Ödemeler',                                        badge: odemeler.length },
        ] as const).map(s => (
          <button key={s.key} onClick={() => setSekme(s.key)} style={{
            flex: 1, padding: '9px 8px', borderRadius: 7, border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: sekme === s.key ? 600 : 400,
            background: sekme === s.key ? '#534AB7' : 'transparent',
            color: sekme === s.key ? 'white' : '#666',
          }}>
            {s.label}
            {s.badge > 0 && (
              <span style={{ marginLeft: 6, background: sekme === s.key ? 'rgba(255,255,255,.25)' : '#eee', borderRadius: 20, padding: '1px 7px', fontSize: 11 }}>
                {s.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Dersler ── */}
      {sekme === 'dersler' && (
        <div style={{ background: 'white', border: '1px solid #eee', borderRadius: 10, overflow: 'hidden' }}>
          {dersler.length === 0
            ? <div style={{ padding: '40px 24px', textAlign: 'center', color: '#aaa', fontSize: 13 }}>
                Bu eğitmene ders atanmamış.
              </div>
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#fafafa', borderBottom: '1px solid #eee' }}>
                    {['Ders', 'Seviye', 'Salon', 'Seanslar', 'Hakedis', 'Kayıtlı'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 16px', fontSize: 11, color: '#aaa', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dersler.map((d, i) => {
                    const [sevBg, sevRenk] = SEV_RENK[d.seviye] ?? ['#f0f0f0', '#666'];
                    const doluluk = d.kapasite > 0 ? Math.round(Number(d.kayitli_ogrenci) / d.kapasite * 100) : 0;
                    return (
                      <tr key={d.id}
                        onClick={() => setSecilenDersId(d.id)}
                        style={{ borderBottom: i < dersler.length - 1 ? '1px solid #f5f5f5' : 'none', cursor: 'pointer' }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'white')}
                      >
                        <td style={{ padding: '10px 16px', fontWeight: 500 }}>{d.ad}</td>
                        <td style={{ padding: '10px 16px' }}>
                          <span style={{ background: sevBg, color: sevRenk, borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{d.seviye}</span>
                        </td>
                        <td style={{ padding: '10px 16px', color: '#666', fontSize: 12 }}>{d.salon_adi || '—'}</td>
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {d.seanslar?.map((s, si) => (
                              <span key={si} style={{ background: '#f0f0f0', color: '#555', borderRadius: 20, padding: '1px 7px', fontSize: 11 }}>
                                {s.gun} {SAAT(s.baslangic_saat)}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <span style={{ background: '#EEEDFE', color: '#534AB7', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                            %{d.hakedis_oran}
                          </span>
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 12, color: '#555' }}>{d.kayitli_ogrenci}/{d.kapasite}</span>
                            <div style={{ width: 40, background: '#f0f0f0', borderRadius: 3, height: 4 }}>
                              <div style={{ height: '100%', borderRadius: 3, background: doluluk >= 90 ? '#e24b4a' : doluluk >= 70 ? '#F5A623' : '#1D9E75', width: `${doluluk}%` }} />
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          }
        </div>
      )}

      {/* ── Programım ── */}
      {sekme === 'program' && (() => {
        type ProgramSeans = { dersId: string; dersAd: string; salon: string; bas: string; bit: string; bg: string; fg: string; };
        const gunMap: Record<string, ProgramSeans[]> = {};
        GUNLER.forEach(g => { gunMap[g] = []; });
        dersler.forEach((d, di) => {
          const [bg, fg] = RENK_PALETI[di % RENK_PALETI.length];
          d.seanslar?.forEach(s => { if (gunMap[s.gun]) gunMap[s.gun].push({ dersId: d.id, dersAd: d.ad, salon: d.salon_adi, bas: s.baslangic_saat, bit: s.bitis_saat, bg, fg }); });
        });
        const seansToplam: typeof gunMap['Pzt'] = [];
        dersler.forEach((d, di) => { const [bg, fg] = RENK_PALETI[di % RENK_PALETI.length]; d.seanslar?.forEach(s => seansToplam.push({ dersId: d.id, dersAd: d.ad, salon: d.salon_adi, bas: s.baslangic_saat, bit: s.bitis_saat, bg, fg })); });
        if (seansToplam.length === 0) return (
          <div style={{ background: 'white', border: '1px solid #eee', borderRadius: 10, padding: '40px 24px', textAlign: 'center', color: '#aaa', fontSize: 13 }}>
            Bu eğitmene ders atanmamış.
          </div>
        );
        const minMin = Math.max(GRID_BAS, Math.min(...seansToplam.map(s => saatToMin(s.bas))) - 30);
        const maxMin = Math.min(GRID_BIT, Math.max(...seansToplam.map(s => saatToMin(s.bit))) + 30);
        const gridSure = maxMin - minMin;
        const gridYuk = Math.ceil(gridSure / 60) * SAAT_YUK;
        const saatler: number[] = [];
        for (let h = Math.floor(minMin / 60); h <= Math.ceil(maxMin / 60); h++) saatler.push(h);

        return (
          <div style={{ background: 'white', border: '1px solid #eee', borderRadius: 10, overflow: 'hidden' }}>
            {/* Gün başlıkları */}
            <div style={{ display: 'grid', gridTemplateColumns: `48px repeat(7, 1fr)`, borderBottom: '1px solid #eee' }}>
              <div />
              {GUNLER.map(g => (
                <div key={g} style={{ padding: '8px 4px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: gunMap[g].length > 0 ? '#534AB7' : '#ccc', background: '#fafafa', borderLeft: '1px solid #f0f0f0' }}>{g}</div>
              ))}
            </div>
            {/* Izgara */}
            <div style={{ display: 'grid', gridTemplateColumns: `48px repeat(7, 1fr)`, position: 'relative' }}>
              {/* Saat sütunu */}
              <div style={{ position: 'relative', height: gridYuk }}>
                {saatler.map(h => (
                  <div key={h} style={{ position: 'absolute', top: (h * 60 - minMin) / gridSure * gridYuk - 7, left: 0, width: '100%', fontSize: 10, color: '#bbb', textAlign: 'right', paddingRight: 6 }}>
                    {String(h).padStart(2, '0')}:00
                  </div>
                ))}
              </div>
              {/* Gün sütunları */}
              {GUNLER.map(g => (
                <div key={g} style={{ position: 'relative', height: gridYuk, borderLeft: '1px solid #f0f0f0', background: gunMap[g].length === 0 ? '#fafafa' : 'white' }}>
                  {/* Yatay saat çizgileri */}
                  {saatler.map(h => (
                    <div key={h} style={{ position: 'absolute', top: (h * 60 - minMin) / gridSure * gridYuk, left: 0, right: 0, borderTop: '1px solid #f5f5f5' }} />
                  ))}
                  {/* Seans blokları */}
                  {gunMap[g].map((s, si) => {
                    const top = (saatToMin(s.bas) - minMin) / gridSure * gridYuk;
                    const height = Math.max(28, (saatToMin(s.bit) - saatToMin(s.bas)) / gridSure * gridYuk - 2);
                    return (
                      <div key={si}
                        onClick={() => setSecilenDersId(s.dersId)}
                        style={{ position: 'absolute', top, left: 3, right: 3, height, background: s.bg, borderLeft: `3px solid ${s.fg}`, borderRadius: 5, padding: '3px 6px', cursor: 'pointer', overflow: 'hidden' }}
                        onMouseEnter={e => (e.currentTarget.style.opacity = '.85')}
                        onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                      >
                        <div style={{ fontSize: 11, fontWeight: 600, color: s.fg, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.dersAd}</div>
                        <div style={{ fontSize: 10, color: s.fg, opacity: .75 }}>{SAAT(s.bas)}–{SAAT(s.bit)}</div>
                        {s.salon && <div style={{ fontSize: 10, color: s.fg, opacity: .6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.salon}</div>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── Hakedis Geçmişi ── */}
      {sekme === 'hakedis' && (
        <div style={{ background: 'white', border: '1px solid #eee', borderRadius: 10, overflow: 'hidden' }}>
          {hakedisDetay.length === 0
            ? <div style={{ padding: '40px 24px', textAlign: 'center', color: '#aaa', fontSize: 13 }}>Henüz yoklama kaydı yok.</div>
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#fafafa', borderBottom: '1px solid #eee' }}>
                    {['Tarih', 'Ders', 'Katıldı', 'Gelmedi', 'Oran', 'Hakedis'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 16px', fontSize: 11, color: '#aaa', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {hakedisDetay.map((h, i) => {
                    const toplam = Number(h.katildi) + Number(h.gelmedi);
                    const oran = toplam > 0 ? Math.round(Number(h.katildi) / toplam * 100) : 0;
                    return (
                      <tr key={i} style={{ borderBottom: i < hakedisDetay.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
                        <td style={{ padding: '10px 16px', color: '#555' }}>{TR(h.tarih)}</td>
                        <td style={{ padding: '10px 16px', fontWeight: 500 }}>{h.ders_adi}</td>
                        <td style={{ padding: '10px 16px' }}>
                          <span style={{ background: '#EAF3DE', color: '#3B6D11', borderRadius: 20, padding: '2px 8px', fontSize: 11 }}>{h.katildi}</span>
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <span style={{ background: '#FCEBEB', color: '#A32D2D', borderRadius: 20, padding: '2px 8px', fontSize: 11 }}>{h.gelmedi}</span>
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 50, background: '#f0f0f0', borderRadius: 4, height: 5 }}>
                              <div style={{ height: '100%', borderRadius: 4, background: oran >= 80 ? '#1D9E75' : oran >= 50 ? '#F5A623' : '#e24b4a', width: `${oran}%` }} />
                            </div>
                            <span style={{ fontSize: 11, color: '#888' }}>%{oran}</span>
                          </div>
                        </td>
                        <td style={{ padding: '10px 16px', fontWeight: 600, color: '#1D9E75' }}>
                          {PARA(Number(h.hakedis))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #eee', background: '#fafafa' }}>
                    <td colSpan={5} style={{ padding: '10px 16px', fontSize: 12, color: '#888', fontWeight: 600 }}>Toplam (son 50 oturum)</td>
                    <td style={{ padding: '10px 16px', fontWeight: 700, color: '#1D9E75', fontSize: 14 }}>
                      {PARA(hakedisDetay.reduce((s, h) => s + Number(h.hakedis), 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )
          }
        </div>
      )}

      {/* ── Ödemeler ── */}
      {sekme === 'odemeler' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: '#888' }}>
              Toplam birikmiş: <strong style={{ color: '#333' }}>{ozet ? PARA(Number(ozet.toplam_birikmis)) : '—'}</strong>
              {' · '}Ödenen: <strong style={{ color: '#1D9E75' }}>{ozet ? PARA(Number(ozet.toplam_odenen)) : '—'}</strong>
              {bekleyen > 0 && <>{' · '}Bekleyen: <strong style={{ color: '#854F0B' }}>{PARA(bekleyen)}</strong></>}
            </div>
            <button onClick={() => { setOdemeHata(''); setOdemeModal(true); }}
              style={{ background: '#534AB7', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              + Ödeme kaydet
            </button>
          </div>

          <div style={{ background: 'white', border: '1px solid #eee', borderRadius: 10, overflow: 'hidden' }}>
            {odemeler.length === 0
              ? <div style={{ padding: '40px 24px', textAlign: 'center', color: '#aaa', fontSize: 13 }}>Henüz ödeme kaydı yok.</div>
              : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#fafafa', borderBottom: '1px solid #eee' }}>
                      {['Tarih', 'Dönem', 'Tip', 'Yöntem', 'Tutar', 'Açıklama'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '8px 16px', fontSize: 11, color: '#aaa', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {odemeler.map((o, i) => (
                      <tr key={o.id} style={{ borderBottom: i < odemeler.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
                        <td style={{ padding: '10px 16px', color: '#555' }}>{TR(o.odeme_tarihi)}</td>
                        <td style={{ padding: '10px 16px', color: '#888', fontSize: 12 }}>
                          {o.donem_baslangic ? `${TR(o.donem_baslangic)} – ${TR(o.donem_bitis)}` : '—'}
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <span style={{ background: '#EEEDFE', color: '#534AB7', borderRadius: 20, padding: '2px 8px', fontSize: 11 }}>{o.odeme_tipi}</span>
                        </td>
                        <td style={{ padding: '10px 16px', color: '#666', fontSize: 12 }}>{o.odeme_yontemi}</td>
                        <td style={{ padding: '10px 16px', fontWeight: 700, color: '#1D9E75' }}>{PARA(Number(o.odenen_tutar))}</td>
                        <td style={{ padding: '10px 16px', color: '#888', fontSize: 12 }}>{o.aciklama || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            }
          </div>
        </div>
      )}

      {/* ── Ders Detay Drawer ── */}
      {secilenDersId && (
        <>
          {/* Overlay */}
          <div onClick={() => setSecilenDersId(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.25)', zIndex: 150 }} />
          {/* Panel */}
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 380, background: 'white', zIndex: 151, boxShadow: '-4px 0 24px rgba(0,0,0,.12)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
              <button onClick={() => setSecilenDersId(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#888', lineHeight: 1, padding: 0 }}>×</button>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{dersDetay?.ders?.ad ?? '...'}</div>
                {dersDetay?.ders?.seviye && (
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{dersDetay.ders.seviye} · {dersDetay.ders.salon_adi ?? ''}</div>
                )}
              </div>
              <button onClick={() => navigate(`/dersler/${secilenDersId}`)}
                style={{ background: '#534AB7', color: 'white', border: 'none', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                Derse git →
              </button>
            </div>

            {dersDetayYuk
              ? <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 13 }}>Yükleniyor...</div>
              : dersDetay && (
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
                  {/* Ders bilgileri */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                    <InfoKutu baslik="Kapasite" deger={`${dersDetay.ogrenciler.length} / ${dersDetay.ders.kapasite}`} />
                    <InfoKutu baslik="Kayıt" deger={dersDetay.ders.kayit_acik ? 'Açık' : 'Kapalı'} renk={dersDetay.ders.kayit_acik ? '#1D9E75' : '#e24b4a'} />
                  </div>
                  {dersDetay.ders.seanslar?.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 10, color: '#aaa', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: .4, marginBottom: 6 }}>Seanslar</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {dersDetay.ders.seanslar.map((s: any, i: number) => (
                          <span key={i} style={{ background: '#f0f0f0', color: '#555', borderRadius: 20, padding: '3px 9px', fontSize: 11 }}>
                            {s.gun} {SAAT(s.baslangic_saat)}–{SAAT(s.bitis_saat)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Öğrenciler */}
                  <div style={{ fontSize: 10, color: '#aaa', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: .4, marginBottom: 8 }}>
                    Kayıtlı Öğrenciler ({dersDetay.ogrenciler.length})
                  </div>
                  {dersDetay.ogrenciler.length === 0
                    ? <div style={{ fontSize: 13, color: '#aaa', textAlign: 'center', padding: '20px 0' }}>Kayıtlı öğrenci yok.</div>
                    : dersDetay.ogrenciler.map(o => (
                      <div key={o.id}
                        onClick={() => navigate(`/ogrenciler/${o.id}`)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 2 }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        {o.foto_url
                          ? <img src={fotoUrl(o.foto_url)} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                          : <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#EEEDFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#534AB7', flexShrink: 0 }}>
                              {o.ad?.charAt(0)}{o.soyad?.charAt(0)}
                            </div>
                        }
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.ad} {o.soyad}</div>
                          <div style={{ fontSize: 11, color: '#aaa' }}>{o.email}</div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          {o.donduruldu
                            ? <span style={{ background: '#f0f0f0', color: '#888', borderRadius: 20, padding: '2px 7px', fontSize: 10 }}>Donduruldu</span>
                            : <span style={{ background: o.kalan_hak === 0 || o.kalan_hak === null ? '#FCEBEB' : '#EAF3DE', color: o.kalan_hak === 0 || o.kalan_hak === null ? '#A32D2D' : '#3B6D11', borderRadius: 20, padding: '2px 7px', fontSize: 10, fontWeight: 600 }}>
                                {o.kalan_hak ?? 0} hak
                              </span>
                          }
                        </div>
                      </div>
                    ))
                  }
                </div>
              )
            }
          </div>
        </>
      )}

      {/* ── Ödeme modalı ── */}
      {bakiyeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
          onClick={e => { if (e.target === e.currentTarget) setBakiyeModal(false); }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: 360 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Devreden Bakiye</div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
              Önceki sistemden devreden hakediş tutarını girin. Bu tutar eğitmenin toplam alacağına eklenir.
            </div>
            <input
              type="number" min="0" step="0.01"
              value={bakiyeInput}
              onChange={e => setBakiyeInput(e.target.value)}
              placeholder="0.00"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 15, marginBottom: 16 }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setBakiyeModal(false)} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #ddd', background: 'white', cursor: 'pointer', fontSize: 13 }}>İptal</button>
              <button onClick={bakiyeKaydet} disabled={bakiyeKaydediliyor}
                style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#854F0B', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                {bakiyeKaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {odemeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) setOdemeModal(false); }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 420 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Ödeme kaydet</div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
              {egitmen.ad} {egitmen.soyad}
            </div>

            {bekleyen > 0 && (
              <button onClick={() => setOdemeForm(p => ({ ...p, odenen_tutar: bekleyen.toFixed(2) }))}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '2px solid #1D9E75', background: '#EAF3DE', color: '#085041', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 14, textAlign: 'left' as const }}>
                ✓ Tüm bekleyeni öde — {PARA(bekleyen)}
              </button>
            )}

            <div style={{ marginBottom: 12 }}>
              <Label>Tutar (₺)</Label>
              <input type="number" value={odemeForm.odenen_tutar}
                onChange={e => setOdemeForm(p => ({ ...p, odenen_tutar: e.target.value }))}
                placeholder="0.00" min={0}
                style={inputSt} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <Label>Açıklama (opsiyonel)</Label>
              <input value={odemeForm.aciklama}
                onChange={e => setOdemeForm(p => ({ ...p, aciklama: e.target.value }))}
                placeholder="Notlar..."
                style={inputSt} />
            </div>

            {odemeHata && (
              <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>
                {odemeHata}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setOdemeModal(false)} style={btnSt}>İptal</button>
              <button onClick={odemeKaydet} disabled={odemeKaydediliyor}
                style={{ ...btnSt, background: '#534AB7', color: 'white', border: 'none' }}>
                {odemeKaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatKart({ baslik, deger, renk, bg }: { baslik: string; deger: string; renk: string; bg: string }) {
  return (
    <div style={{ background: bg, borderRadius: 10, padding: '14px 16px', border: `1px solid ${bg}` }}>
      <div style={{ fontSize: 10, color: renk, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: .4, marginBottom: 6, opacity: .8 }}>{baslik}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: renk }}>{deger}</div>
    </div>
  );
}

function InfoKutu({ baslik, deger, renk }: { baslik: string; deger: string; renk?: string }) {
  return (
    <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 12px', border: '1px solid #f0f0f0' }}>
      <div style={{ fontSize: 10, color: '#aaa', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: .3, marginBottom: 3 }}>{baslik}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: renk ?? '#333' }}>{deger}</div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 11, color: '#888', marginBottom: 4, fontWeight: 500 }}>{children}</div>;
}

const inputSt: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box',
};
const selectSt: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13,
};
const btnSt: React.CSSProperties = {
  padding: '8px 18px', borderRadius: 6, border: '1px solid #ddd', background: 'white', cursor: 'pointer', fontSize: 13,
};
